import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Star, Download, Plus, Loader2, Film, AlertCircle, Clock, X, Edit3, Sparkles, RotateCcw } from 'lucide-react';
import HeygenEditModal from './HeygenEditModal';
import toast from 'react-hot-toast';
import { videoStudioService } from '../../services/videoStudioService';
import { moveMediaToFolder } from '../../services/mediaFolderService';
import { GeneratedVideo, VideoProvider } from '../../types/videoStudio';
import FolderSidebar from '../media/FolderSidebar';

interface Props {
  refreshKey: number;
  onAddToTimeline: (video: GeneratedVideo) => void;
  onSelect?: (video: GeneratedVideo) => void;
  onRegenerate?: (video: GeneratedVideo) => void;
  onCreate?: () => void; // botão do estado vazio (vai para "Gerar")
}

const PROVIDER_BADGE: Record<VideoProvider, { label: string; classes: string }> = {
  seedance: { label: 'Cena IA', classes: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
  heygen: { label: 'Apresentador', classes: 'bg-violet-500/20 text-violet-300 border-violet-500/30' },
  composition: { label: 'Edição', classes: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  uploaded: { label: 'Importado', classes: 'bg-gray-500/20 text-gray-700 dark:text-gray-300 border-gray-500/30' }
};

const VideoGallery: React.FC<Props> = ({ refreshKey, onAddToTimeline, onSelect, onCreate }) => {
  const { t } = useTranslation();
  const [videos, setVideos] = useState<GeneratedVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | VideoProvider | 'pending'>('all');
  const [preview, setPreview] = useState<GeneratedVideo | null>(null);
  // Edit-and-regenerate modal — only available for HeyGen-generated videos
  const [editing, setEditing] = useState<GeneratedVideo | null>(null);
  // null = todas; 'none' = sem pasta; UUID = pasta específica
  const [selectedFolderId, setSelectedFolderId] = useState<string | null | 'none'>(null);
  const [sidebarRefresh, setSidebarRefresh] = useState(0);
  const draggedVideoIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filter === 'pending') params.status = 'processing';
      else if (filter !== 'all') params.provider = filter;
      if (selectedFolderId !== null) params.folder_id = selectedFolderId;
      const r = await videoStudioService.listVideos(params);
      if (r.success) setVideos(r.data || []);
    } catch (e: any) {
      toast.error(e.message || t('videoStudio.gallery.loadFail'));
    } finally {
      setLoading(false);
    }
  }, [filter, selectedFolderId]);

  const handleDragStart = (e: React.DragEvent, video: GeneratedVideo) => {
    draggedVideoIdRef.current = video.id;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-media-id', video.id);
    e.dataTransfer.setData('text/plain', video.id);
  };

  const handleDropOnFolder = async (folderId: string | null) => {
    const videoId = draggedVideoIdRef.current;
    draggedVideoIdRef.current = null;
    if (!videoId) return;
    try {
      await moveMediaToFolder({ folder_id: folderId, video_ids: [videoId] });
      setVideos((prev) => prev.filter((v) => {
        if (v.id !== videoId) return true;
        if (selectedFolderId === null) return true;
        if (selectedFolderId === 'none') return folderId === null;
        return selectedFolderId === folderId;
      }));
      setSidebarRefresh((n) => n + 1);
      toast.success(t('mediaFolders.moved', 'Movido'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('mediaFolders.moveError', 'Erro ao mover'));
    }
  };

  useEffect(() => { load(); }, [load, refreshKey]);

  // Auto-refresh while there are pending/processing videos so the UI updates
  // when the worker finishes without the user having to click reload.
  useEffect(() => {
    const stillRunning = videos.some(v => ['pending', 'queued', 'processing'].includes(v.status));
    if (!stillRunning) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [videos, load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('videoStudio.gallery.deleteConfirm'))) return;
    try {
      await videoStudioService.remove(id);
      setVideos(prev => prev.filter(v => v.id !== id));
      toast.success(t('videoStudio.gallery.deleted'));
    } catch (e: any) {
      toast.error(e.message || t('videoStudio.gallery.deleteFail'));
    }
  };

  const handleFavorite = async (v: GeneratedVideo) => {
    try {
      const updated = await videoStudioService.update(v.id, { is_favorite: !v.is_favorite });
      if (updated.success && updated.data) {
        setVideos(prev => prev.map(x => x.id === v.id ? updated.data! : x));
      }
    } catch { /* silent */ }
  };

  const filterTabs: { value: typeof filter; label: string }[] = [
    { value: 'all', label: t('videoStudio.gallery.filterAll') },
    { value: 'seedance', label: t('videoStudio.gallery.filterScenes') },
    { value: 'heygen', label: t('videoStudio.gallery.filterPresenters') },
    { value: 'composition', label: t('videoStudio.gallery.filterCompositions') },
    { value: 'pending', label: t('videoStudio.gallery.filterPending') }
  ];

  return (
    <div className="flex flex-col md:flex-row gap-4">
      <aside className="w-full md:w-56 flex-shrink-0">
        <div className="md:sticky md:top-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 space-y-1">
          <FolderSidebar
            key={sidebarRefresh}
            mediaType="video"
            selectedFolderId={selectedFolderId}
            onSelect={setSelectedFolderId}
            onDropOnFolder={handleDropOnFolder}
          />
        </div>
      </aside>
      <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-1">
        {filterTabs.map(t => (
          <button
            key={t.value}
            onClick={() => setFilter(t.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition ${
              filter === t.value
                ? 'bg-primary-500/20 text-primary-300 border border-primary-500/30'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 border border-transparent'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-6 h-6 text-primary-400 animate-spin" />
        </div>
      ) : videos.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 text-center rounded-2xl bg-gray-50 dark:bg-gray-900/30 border border-dashed border-gray-200 dark:border-gray-800">
          <Film className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" />
          <p className="text-gray-900 dark:text-white font-semibold">{t('videoStudio.gallery.empty')}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('videoStudio.gallery.emptyHint')}</p>
          {onCreate && (
            <button
              onClick={onCreate}
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              {t('videoStudio.gallery.emptyCta', 'Criar vídeo com IA')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {videos.map(v => {
            const badge = PROVIDER_BADGE[v.provider];
            const isProcessing = ['pending', 'queued', 'processing'].includes(v.status);
            const isFailed = v.status === 'failed';
            return (
              <div
                key={v.id}
                draggable
                onDragStart={(e) => handleDragStart(e, v)}
                className="group relative rounded-2xl overflow-hidden bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:border-gray-700 transition"
              >
                <div
                  className="bg-black relative cursor-pointer flex items-center justify-center aspect-video max-h-72"
                  onClick={() => setPreview(v)}
                >
                  {v.original_url && !isProcessing ? (
                    <video
                      src={v.original_url}
                      poster={v.thumbnail_url || undefined}
                      // Com poster não baixa NADA do vídeo até o hover; sem poster,
                      // só o metadata (1º frame) — antes cada card podia puxar o arquivo.
                      preload={v.thumbnail_url ? 'none' : 'metadata'}
                      muted
                      playsInline
                      onMouseEnter={(e) => (e.currentTarget as HTMLVideoElement).play().catch(() => {})}
                      onMouseLeave={(e) => {
                        const el = e.currentTarget as HTMLVideoElement;
                        el.pause(); el.currentTime = 0;
                      }}
                      className="w-full h-full object-contain"
                    />
                  ) : v.thumbnail_url ? (
                    <img src={v.thumbnail_url} alt="" className="w-full h-full object-contain opacity-50" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950">
                      {isProcessing ? (
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 text-primary-400 animate-spin mx-auto mb-2" />
                          <p className="text-xs text-gray-600 dark:text-gray-400">{v.progress}%</p>
                        </div>
                      ) : isFailed ? (
                        <div className="text-center px-3">
                          <AlertCircle className="w-8 h-8 text-rose-400 mx-auto mb-2" />
                          <p className="text-[10px] text-gray-600 dark:text-gray-400">{t('videoStudio.gallery.failed')}</p>
                        </div>
                      ) : (
                        <Film className="w-10 h-10 text-gray-700" />
                      )}
                    </div>
                  )}

                  {/* Status overlay */}
                  {isProcessing && (
                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-gray-950 via-gray-950/80">
                      <div className="flex items-center gap-2 text-xs text-white mb-1">
                        <Clock className="w-3 h-3" />
                        {t('videoStudio.gallery.processing')}
                      </div>
                      <div className="w-full h-1 rounded-full bg-gray-800 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-primary-500 to-purple-500 transition-all" style={{ width: `${v.progress}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Quick actions */}
                  <div className="absolute top-2 left-2 flex gap-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold border ${badge.classes}`}>
                      {badge.label}
                    </span>
                    {v.aspect_ratio && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white dark:bg-gray-900/80 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-800">
                        {v.aspect_ratio}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleFavorite(v); }}
                    className={`absolute top-2 right-2 p-1.5 rounded-lg backdrop-blur transition ${
                      v.is_favorite ? 'bg-amber-500 text-amber-950' : 'bg-white dark:bg-gray-900/80 text-gray-600 dark:text-gray-400 opacity-0 group-hover:opacity-100'
                    }`}
                  >
                    <Star className={`w-3.5 h-3.5 ${v.is_favorite ? 'fill-current' : ''}`} />
                  </button>
                </div>

                <div className="p-3">
                  <p className="text-sm font-medium text-white truncate">{v.title || t('videoStudio.gallery.untitled')}</p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {v.duration_seconds ? `${Math.round(v.duration_seconds)}s · ` : ''}
                    {t('videoStudio.gallery.credits', { count: v.credits_cost })}
                  </p>
                  <div className="flex items-center justify-between gap-1 mt-2.5">
                    <button
                      onClick={() => onAddToTimeline(v)}
                      disabled={!v.original_url}
                      className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 rounded-lg bg-primary-500/10 text-primary-300 border border-primary-500/30 text-xs font-semibold hover:bg-primary-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {t('videoStudio.gallery.addToTimeline', 'Adicionar')}
                    </button>
                    {/* Regenerate-with-new-script button — only for HeyGen presenter videos.
                        Other providers (Seedance, uploaded, composition) can't regenerate
                        the same way because their input isn't a script. */}
                    {v.provider === 'heygen' && v.status === 'completed' && (
                      <button
                        onClick={() => setEditing(v)}
                        className="p-1.5 rounded-lg bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/30 hover:bg-violet-500/25 transition"
                        title={t('videoStudio.heygenEdit.btnTitle', 'Editar roteiro e regerar') as string}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {v.original_url && (
                      <a
                        href={v.original_url}
                        download
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 rounded-lg bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white transition"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      onClick={() => handleDelete(v.id)}
                      className="p-1.5 rounded-lg bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-rose-500/20 hover:text-rose-300 transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox preview */}
      {preview && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-950/90 backdrop-blur-sm"
          onClick={() => setPreview(null)}
        >
          <button
            onClick={() => setPreview(null)}
            className="absolute top-4 right-4 p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {preview.original_url && (
              <video
                src={preview.original_url}
                poster={preview.thumbnail_url || undefined}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] rounded-xl shadow-2xl"
                style={{ aspectRatio: (preview.aspect_ratio || '16:9').replace(':', ' / ') }}
              />
            )}
            <div className="absolute -bottom-12 left-0 right-0 text-center">
              <p className="text-white text-sm font-medium truncate">{preview.title || t('videoStudio.gallery.untitled')}</p>
              <p className="text-xs text-gray-400">
                {preview.aspect_ratio} · {preview.duration_seconds ? `${Math.round(preview.duration_seconds)}s · ` : ''}{preview.credits_cost} {t('videoStudio.credits.creditsUnit')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* HeyGen edit & regenerate modal — opens from the violet "Editar" button */}
      {editing && (
        <HeygenEditModal
          video={editing}
          onClose={() => setEditing(null)}
          onRegenerated={(v) => { setEditing(null); load(); /* refresh gallery */ }}
        />
      )}
      </div>
    </div>
  );
};

export default VideoGallery;
